import { findAndFocusElement } from '@/features/navigation/utils/find-and-focus-element';
import { useBreakpointDown, useBreakpointUp } from '@/hooks/use-theme';
import { Redirect, getRedirectPath } from '@/utils/redirects';
import {
  useDebouncedState,
  useKeyboardEvent,
  useToggle,
} from '@react-hookz/web';
import { useLocale } from 'next-intl';
import { type Dispatch, type FC, type SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DesktopSearch } from './desktop-search';
import { MobileSearch } from './mobile-search';

interface Props {
  setShouldHideVoucherButton: Dispatch<SetStateAction<boolean>>;
}

export interface SearchFormValues {
  query: string;
  isFocused: boolean;
}

export const Search: FC<Props> = ({ setShouldHideVoucherButton }) => {
  const isMobile = useBreakpointDown('m');
  const locale = useLocale();
  const isBreakpointHigherThanXl = useBreakpointUp('xl');
  const [isModalOpen, toggleModalOpen] = useToggle(false);
  const { control, reset, watch, setFocus, setValue } =
    useForm<SearchFormValues>({
      mode: 'onBlur',
      defaultValues: {
        query: '',
        isFocused: false,
      },
    });

  const query = watch('query');
  const isFocused = watch('isFocused');
  const [debouncedQuery, setDebouncedQuery] = useDebouncedState(query, 500);

  const clearQuery = () => {
    if (isMobile && isModalOpen) {
      findAndFocusElement('[data-ui="mobile-search-button"]');
    }
    setDebouncedQuery('');
    reset({ query: '' });
    setFocus('query');
  };

  const handleChangeQuery = (query: string) => {
    setDebouncedQuery(query);
  };

  const isDesktopSearchExpanded = query?.length >= 1 && !isMobile;

  useEffect(() => {
    if (typeof window != 'undefined' && window.document) {
      document.body.style.setProperty(
        'overflow-y',
        isDesktopSearchExpanded ? 'hidden' : 'scroll',
        'important',
      );
    }

    return () => {
      document.body.style.setProperty('overflow-y', 'scroll', 'important');
    };
  }, [isDesktopSearchExpanded]);

  useEffect(() => {
    if ((isDesktopSearchExpanded || isFocused) && !isBreakpointHigherThanXl) {
      setShouldHideVoucherButton(true);
    } else {
      setShouldHideVoucherButton(false);
    }
  }, [
    isDesktopSearchExpanded,
    isBreakpointHigherThanXl,
    setShouldHideVoucherButton,
    isFocused,
  ]);

  const navigateToSearchResult = () => {
    if (typeof window == 'undefined') return;

    if (isModalOpen || isDesktopSearchExpanded || isFocused) {
      //This is the navigation to external page, according to docs(https://nextjs.org/docs/pages/api-reference/functions/use-router) we should use window.location here
      window.location.href = `${getRedirectPath(Redirect.SEARCH, locale)}?search_data=${query}`;
    }
  };

  useKeyboardEvent('Escape', clearQuery);
  useKeyboardEvent('Enter', navigateToSearchResult);

  const toggleFocus = (focused: boolean) => {
    setValue('isFocused', focused);
  };

  const handleOpenModal = () => toggleModalOpen(true);
  const handleCloseModal = () => {
    toggleModalOpen(false);
    clearQuery();
  };

  return (
    <>
      <MobileSearch
        debouncedQuery={debouncedQuery}
        handleChangeQuery={handleChangeQuery}
        control={control}
        toggleFocus={toggleFocus}
        isModalOpen={isModalOpen}
        handleOpenModal={handleOpenModal}
        handleCloseModal={handleCloseModal}
      />
      <DesktopSearch
        debouncedQuery={debouncedQuery}
        handleChangeQuery={handleChangeQuery}
        isDesktopSearchExpanded={isDesktopSearchExpanded}
        reset={clearQuery}
        control={control}
        toggleFocus={toggleFocus}
      />
    </>
  );
};
